import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { invoicesApi } from '../api/invoices';
import type { Invoice } from '@coreledger/shared-types';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const LR_BILLED_CLS: Record<string, string> = { billed: 'badge-paid', unbilled: 'badge-unpaid' };

export default function LorryReceiptsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { canDelete } = useRole();
  const [search, setSearch] = useState('');
  const [lrStatus, setLrStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [raising, setRaising] = useState(false);

  const { data: allInvoicesResult, isLoading } = useQuery({
    queryKey: ['invoices', '', 'lorry_receipt'],
    queryFn: () => invoicesApi.list({ docType: 'lorry_receipt' }, 1, 200),
    staleTime: 30 * 1000,
  });
  const allInvoices = allInvoicesResult?.data ?? [];

  const del = useMutation({
    mutationFn: invoicesApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ queryKey: ['sales-summary'], type: 'all' });
      qc.refetchQueries({ queryKey: ['outstanding'], type: 'all' });
      qc.refetchQueries({ type: 'all' });
    },
  });

  const lrs = allInvoices.filter(inv => {
    const q = search.toLowerCase();
    const matchSearch = !q || (inv.invoiceNumber ?? '').toLowerCase().includes(q)
      || inv.partyName.toLowerCase().includes(q)
      || (inv.vehicleNumber ?? '').toLowerCase().includes(q)
      || (inv.fromCity ?? '').toLowerCase().includes(q)
      || (inv.toCity ?? '').toLowerCase().includes(q)
      || (inv.lrNumber ?? '').toLowerCase().includes(q);
    const matchStatus = !lrStatus || (lrStatus === 'unbilled' ? inv.lrStatus !== 'billed' : inv.lrStatus === lrStatus);
    return matchSearch && matchStatus;
  });

  const unbilledCount = allInvoices.filter(i => i.lrStatus !== 'billed').length;
  const billedCount = allInvoices.filter(i => i.lrStatus === 'billed').length;
  const totalFreight = allInvoices.reduce((s, i) => s + i.total, 0);

  async function handleDelete(inv: Invoice) {
    if (await confirm({ message: `Delete LR ${inv.invoiceNumber || inv.lrNumber || ''}?`, danger: true })) del.mutate(inv.id);
  }

  function handleNewLr() {
    navigate('/lorry-receipts/new');
  }

  function exportCsv() {
    const label = lrStatus === 'billed' ? 'Billed' : lrStatus === 'unbilled' ? 'Unbilled' : 'All';
    const rows = [
      ['LR #', 'Date', 'Party (Consignor)', 'Consignee', 'From', 'To', 'Vehicle #', 'Freight (₹)', 'GST (₹)', 'Total (₹)', 'Status'],
      ...lrs.map(lr => {
        const trip0 = lr.trips?.[0];
        const freight = lr.subtotal ?? 0;
        const gst = lr.taxTotal ?? 0;
        return [
          lr.invoiceNumber ?? lr.lrNumber ?? '',
          lr.date ?? '',
          lr.partyName ?? '',
          lr.consigneeName ?? '',
          trip0?.fromCity ?? lr.fromCity ?? '',
          trip0?.toCity ?? lr.toCity ?? '',
          trip0?.vehicleNumber ?? lr.vehicleNumber ?? '',
          freight.toFixed(2),
          gst.toFixed(2),
          (lr.total ?? 0).toFixed(2),
          lr.lrStatus === 'billed' ? 'Billed' : 'Unbilled',
        ];
      }),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `LR-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Lorry Receipts</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {selectedIds.size > 0 && (
            <button className="btn btn-primary btn-sm" disabled={raising} onClick={async () => {
              const selectedLrs = lrs.filter(lr => selectedIds.has(lr.id) && lr.lrStatus !== 'billed');
              if (!selectedLrs.length) { alert('All selected LRs are already billed'); return; }
              setRaising(true);
              try {
                const invoice = await invoicesApi.raiseFromLRs(selectedLrs.map(lr => lr.id));
                qc.invalidateQueries({ queryKey: ['invoices'] });
                qc.refetchQueries({ type: 'all' });
                setSelectedIds(new Set());
                navigate(`/invoices/${invoice.id}`);
              } catch (e: unknown) {
                alert((e as Error).message ?? 'Failed to raise invoice');
              } finally {
                setRaising(false);
              }
            }}>
              {raising ? <span className="spinner" style={{ width: 14, height: 14 }} /> : `🧾 Raise Invoice from ${selectedIds.size} LR${selectedIds.size > 1 ? 's' : ''}`}
            </button>
          )}
          <button className="btn btn-ghost" onClick={exportCsv} disabled={lrs.length === 0} title="Export current list to CSV">⬇ Export CSV</button>
          <button className="btn btn-primary" onClick={handleNewLr}>+ New LR</button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
        <div className="card" style={{ padding: '1rem 1.25rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Total LRs</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)' }}>{allInvoices.length}</div>
        </div>
        <div className="card" style={{ padding: '1rem 1.25rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Unbilled</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--warning)' }}>{unbilledCount}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{billedCount} billed</div>
        </div>
        <div className="card" style={{ padding: '1rem 1.25rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Total Freight</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{fmt(totalFreight)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search LR#, party, vehicle, city…" style={{ maxWidth: 300 }} />
        <select value={lrStatus} onChange={e => setLrStatus(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All</option>
          <option value="unbilled">Unbilled</option>
          <option value="billed">Billed</option>
        </select>
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : lrs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🚚</div>
              <div className="empty-state-text">{allInvoices.length === 0 ? 'No lorry receipts yet' : 'No LRs match current filters'}</div>
              {allInvoices.length === 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <button className="btn btn-primary" onClick={handleNewLr}>+ Create First LR</button>
                </div>
              )}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                      checked={lrs.filter(lr => lr.lrStatus !== 'billed').length > 0 && lrs.filter(lr => lr.lrStatus !== 'billed').every(lr => selectedIds.has(lr.id))}
                      onChange={e => {
                        const unbilled = lrs.filter(lr => lr.lrStatus !== 'billed').map(lr => lr.id);
                        setSelectedIds(e.target.checked ? new Set(unbilled) : new Set());
                      }}
                    />
                  </th>
                  <th>LR #</th>
                  <th>Date</th>
                  <th>Party (Consignor)</th>
                  <th>Vehicle</th>
                  <th>From → To</th>
                  <th>Driver</th>
                  <th style={{ textAlign: 'right' }}>Freight</th>
                  <th>Billing</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lrs.map(inv => (
                  <tr key={inv.id} style={{ background: selectedIds.has(inv.id) ? 'var(--primary-light)' : undefined }}>
                    <td style={{ width: 36 }}>
                      {inv.lrStatus !== 'billed' && (
                        <input type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                          checked={selectedIds.has(inv.id)}
                          onChange={e => setSelectedIds(prev => { const s = new Set(prev); e.target.checked ? s.add(inv.id) : s.delete(inv.id); return s; })}
                        />
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline dotted' }}
                        onClick={() => navigate(`/invoices/${inv.id}`)}>
                        {inv.lrNumber || inv.invoiceNumber || '—'}
                      </span>
                    </td>
                    <td>{inv.date}</td>
                    <td style={{ fontWeight: 500 }}>{inv.partyName}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>{inv.vehicleNumber ?? '—'}</td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {inv.fromCity && inv.toCity ? `${inv.fromCity} → ${inv.toCity}` : inv.fromCity ?? inv.toCity ?? '—'}
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{inv.driverName ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(inv.total)}</td>
                    <td>
                      <span className={`badge ${LR_BILLED_CLS[inv.lrStatus ?? 'unbilled'] ?? 'badge-gray'}`}>
                        {inv.lrStatus === 'billed' ? 'Billed' : 'Unbilled'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/invoices/${inv.id}`)}>View</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/lorry-receipts/${inv.id}/edit`)}>Edit</button>
                        {inv.lrStatus !== 'billed' && (
                          <button className="btn btn-primary btn-sm" title="Create Tax Invoice from this LR" disabled={raising}
                            onClick={async () => {
                              setRaising(true);
                              try {
                                const invoice = await invoicesApi.raiseFromLRs([inv.id]);
                                qc.invalidateQueries({ queryKey: ['invoices'] });
                                qc.refetchQueries({ type: 'all' });
                                navigate(`/invoices/${invoice.id}`);
                              } catch (e: unknown) {
                                alert((e as Error).message ?? 'Failed to raise invoice');
                              } finally { setRaising(false); }
                            }}>
                            Convert
                          </button>
                        )}
                        {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={() => handleDelete(inv)}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
